import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { applyTypography, BaseButton, Input, useT } from '@repo/common';

/**
 * 예약 전 사전 질문 모달.
 *
 * <p>호스트가 미리 알아야 하는 정보(알러지 등)를 예약 확인으로 넘어가기 전에 받는다.
 * 필수 질문에 답하기 전에는 진행할 수 없고, 오버레이를 눌러도 닫히지 않는다 —
 * 실수로 닫고 답 없이 넘어가는 일이 없어야 하기 때문이다. 나가려면 Cancel 을 눌러야 한다.
 *
 * @param {boolean} isOpen
 * @param {Array} questions  [{ id, label, type: 'TEXT'|'YES_NO'|'CHOICE', options, required }]
 * @param {object} initialAnswers  questionId → 값. 같은 워크숍을 다시 예약할 때 채워준다
 * @param {function} onCancel
 * @param {function} onSubmit  (answers: { [questionId]: string }) => void
 * @param {boolean} submitting
 */
const IntakeQuestionsModal = ({
  isOpen,
  questions = [],
  initialAnswers = {},
  onCancel,
  onSubmit,
  submitting = false,
}) => {
  const t = useT();
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    if (!isOpen) return;
    // 이전 답변을 깔고 시작한다. 없는 질문은 빈 값
    const seeded = {};
    questions.forEach((question) => {
      seeded[question.id] = initialAnswers?.[question.id] ?? '';
    });
    setAnswers(seeded);
    // questions/initialAnswers 는 열릴 때의 값만 쓰면 된다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const isIncomplete = useMemo(
    () =>
      questions.some(
        (question) =>
          question.required && !String(answers[question.id] ?? '').trim(),
      ),
    [questions, answers],
  );

  if (!isOpen) return null;

  const setAnswer = (questionId, value) =>
    setAnswers((prev) => ({ ...prev, [questionId]: value }));

  const handleSubmit = () => {
    if (isIncomplete || submitting) return;
    const trimmed = {};
    Object.entries(answers).forEach(([questionId, value]) => {
      const text = String(value ?? '').trim();
      if (text) trimmed[questionId] = text;
    });
    onSubmit?.(trimmed);
  };

  const renderField = (question) => {
    const value = answers[question.id] ?? '';

    if (question.type === 'YES_NO' || question.type === 'CHOICE') {
      const options =
        question.type === 'YES_NO'
          ? [
              { value: 'Yes', label: t('Yes', '예') },
              { value: 'No', label: t('No', '아니오') },
            ]
          : (question.options ?? []).map((option) => ({
              value: option,
              label: option,
            }));

      return (
        <div className='flex flex-wrap gap-2'>
          {options.map((option) => {
            const selected = value === option.value;
            return (
              <button
                key={option.value}
                type='button'
                onClick={() => setAnswer(question.id, option.value)}
                className={`rounded-full border px-4 py-2 typo-b14 transition-colors ${
                  selected
                    ? 'border-green-deep02 bg-green-light01 text-green-deep03'
                    : 'border-grayscale-gray03 bg-white text-text-primary hover:border-grayscale-gray04'
                }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      );
    }

    return (
      <div className='pb-5'>
        <Input
          as='textarea'
          label=''
          value={value}
          onChange={(event) => setAnswer(question.id, event.target.value)}
          placeholder={t('Your answer', '답변을 입력해 주세요')}
          maxLength={1000}
          textareaMinHeight='88px'
          textareaRows={3}
        />
      </div>
    );
  };

  return ReactDOM.createPortal(
    <>
      {/* 오버레이 — 클릭해도 닫지 않는다 */}
      <div className='fixed inset-0 z-[9998] bg-surface-black40' />

      <div className='fixed inset-0 z-[9999] flex items-center justify-center px-4 py-6'>
        <div className='flex max-h-[85vh] w-full max-w-[560px] flex-col rounded-[24px] bg-grayscale-gray01 shadow-2'>
          <div className='px-6 pb-4 pt-6'>
            <p
              className={`${applyTypography('b18Strong', 'intake')} text-text-primary`}
            >
              {t('Before you book', '예약 전 확인해 주세요')}
            </p>
            <p className='mt-1 typo-b13 text-text-placeholder'>
              {t(
                'The host needs a few details to prepare for your visit.',
                '호스트가 준비할 수 있도록 몇 가지만 알려주세요.',
              )}
            </p>
          </div>

          <div className='flex-1 overflow-y-auto px-6 pb-2'>
            <div className='flex flex-col gap-5'>
              {questions.map((question) => (
                <div key={question.id}>
                  <p className='mb-2 typo-b14Strong text-text-primary'>
                    {question.label}
                    {question.required && (
                      <span className='ml-0.5 text-red-600'>*</span>
                    )}
                  </p>
                  {renderField(question)}
                </div>
              ))}
            </div>
          </div>

          <div className='flex items-center justify-end gap-2 px-6 pb-6 pt-4'>
            <BaseButton
              type='text'
              size='m'
              htmlType='button'
              hideIcon
              typo={false}
              onClick={onCancel}
              className='h-auto min-w-0 rounded-lg border-0 bg-transparent px-4 py-2 typo-b14Strong text-text-secondary hover:bg-grayscale-gray02'
            >
              {t('Cancel', '취소')}
            </BaseButton>
            <BaseButton
              type='primary'
              size='m'
              hideIcon
              width='140px'
              isDisabled={isIncomplete || submitting}
              onClick={handleSubmit}
            >
              {t('Continue', '계속하기')}
            </BaseButton>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
};

export default IntakeQuestionsModal;
